// Thin, typed auth API client (P0-13). A hand-rolled fetch layer over the
// live `/v1/auth/*` endpoints (spec §6.1) that:
//   - prefixes the runtime-config `apiBase` (see `configureApiBase`, wired in
//     `../boot` from `GET /bff/runtime-config`),
//   - attaches the in-memory access token (`./session`) as a Bearer header,
//   - sends `credentials: 'include'` so the HttpOnly refresh cookie rides
//     along on `/refresh`/`/logout`,
//   - parses an RFC 7807 `application/problem+json` error body into a typed
//     {@link ApiError} (with the machine-matchable {@link ErrorCode}),
//   - validates each response against the shared zod DTO so a contract drift
//     is caught at the boundary, not deep in a component.
//
// This is a deliberate stand-in for the generated `@eskayscience/api-client`
// (P0-15, still a placeholder): every call site imports the `authApi` object,
// so swapping the implementation to the Orval client later is a one-file
// change here — no view/store touches the transport directly.
import {
  assetResponseSchema,
  authMeResponseSchema,
  examBoardsPublicResponseSchema,
  genericAckResponseSchema,
  levelsPublicResponseSchema,
  loginResponseSchema,
  meResourceDetailSchema,
  refreshResponseSchema,
  registerResponseSchema,
  type AssetResponse,
  type AuthMeResponse,
  type CommandAckRequest,
  type ExamBoardsPublicResponse,
  type GenericAckResponse,
  type InviteAcceptRequest,
  type LevelsPublicResponse,
  type LoginRequest,
  type LoginResponse,
  type MeResourceDetail,
  type PasswordForgotRequest,
  type PasswordResetRequest,
  type RefreshResponse,
  type RegisterRequest,
  type RegisterResponse,
  type ResendVerificationRequest,
  type VerifyEmailRequest,
} from '@eskayscience/shared/dto';
import { isProblemDetails, type ErrorCode, type ProblemDetails } from '@eskayscience/shared/errors';
import { z, type ZodType } from 'zod';

import { getAccessToken } from './session';

// ─── API base (set once at boot from runtime-config) ──────────────────────

let apiBase = '/v1';

/** Point the client at the runtime-config `apiBase` (relative `/v1` in
 * production single-origin deploys, an absolute URL only if ever needed in
 * local dev). Called once from `../boot` after `configStore.load()`. */
export function configureApiBase(base: string | undefined | null): void {
  apiBase = base && base.length > 0 ? base.replace(/\/$/, '') : '/v1';
}

export function getApiBase(): string {
  return apiBase;
}

/** The API ORIGIN with the `/v1` version segment stripped off, for consumers
 * whose request paths ALREADY include `/v1` — notably the generated
 * `@eskayscience/api-client`, whose orval-baked URLs are `'/v1/exam-boards'` etc.
 * Feeding that client the full `apiBase` ('/v1') would double-prefix to
 * `/v1/v1/...` → 404 (P1-BUG-9). Same-origin relative base `/v1` → `''`
 * (empty = axios resolves each `/v1/...` URL against the page origin); an
 * absolute `https://host/v1` → `https://host`. Anything not ending in `/v1`
 * is returned unchanged. */
export function getApiOrigin(): string {
  return apiBase.replace(/\/v1$/, '');
}

/** Full-page-redirect entry point for "Continue with Google" (§6.1
 * `/v1/auth/google/start`): NOT an XHR/fetch — callers set
 * `window.location.href` to this URL so the browser navigates away to the
 * API, which redirects on to Google and, after callback, redirects the
 * browser back into the SPA with a session cookie already set. Built off the
 * same resolved `apiBase` as every other API call so it works unmodified
 * across localhost/127.0.0.1 dev and both production hosts. */
export function getGoogleAuthStartUrl(): string {
  return `${apiBase}/auth/google/start`;
}

// ─── Error types ──────────────────────────────────────────────────────────

/** A non-2xx response carrying (usually) an RFC 7807 problem body. Components
 * branch on `err.code` (never the human `detail`/`title`) and surface
 * `err.userMessage` to the user. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: ProblemDetails | null,
    message: string,
    /** Parsed `Retry-After` response header in seconds, if the server sent one (used by the
     * P2-25 command-poll backoff on `429 RATE_LIMITED`, design §5.2). `null` when absent or
     * unparseable — callers fall back to the standard backoff table in that case. */
    readonly retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get code(): ErrorCode | null {
    return this.problem?.code ?? null;
  }

  /** Best human-readable string for inline display: the occurrence `detail`,
   * else the problem `title`, else a generic fallback. */
  get userMessage(): string {
    return this.problem?.detail ?? this.problem?.title ?? this.message;
  }
}

/** The request never reached (or never got a response from) the server —
 * DNS/offline/CORS/abort. Distinct from {@link ApiError} so callers can show
 * a "check your connection" message rather than a server-error one. */
export class NetworkError extends Error {
  constructor(message = 'Network request failed', options?: ErrorOptions) {
    super(message, options);
    this.name = 'NetworkError';
  }
}

// ─── Core fetch ───────────────────────────────────────────────────────────

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Attach the Bearer access token (default true). Public auth endpoints
   * (login/register/refresh/…) pass `false` — they never need it and must
   * not fail merely because no session exists yet. */
  auth?: boolean;
  signal?: AbortSignal;
  /** `Idempotency-Key` header (optional server-side — see
   * `apps/api/src/middleware/idempotency.ts`) for a mutating call. Callers
   * generate one per logical submission (e.g. `crypto.randomUUID()`) so a
   * retried network failure can't double-create/double-assign. */
  idempotencyKey?: string;
  /**
   * `fetch`'s `keepalive` flag (BOOT-RACE-1). Without it, a `POST
   * /auth/refresh` in flight when the SAME TAB navigates again (a second
   * hard reload / `page.reload()` fired before the first one's refresh
   * settles — proven live with Playwright: ~50% of back-to-back reloads on
   * one authenticated session) gets aborted CLIENT-SIDE by the browser, but
   * the request had already reached the Worker, which completes the
   * rotation anyway (revokes the presented refresh-token row, mints a
   * replacement) — the browser never applies the replacement `Set-Cookie`
   * because the response was discarded. The NEXT reload's boot then
   * presents that now-already-rotated cookie, which the server's reuse
   * detector (spec §10) correctly treats as a stolen-token replay and
   * revokes the WHOLE token family — permanently logging the user out until
   * a fresh `/login`, not just for that one reload. `keepalive: true` lets
   * this specific request (and its response, including `Set-Cookie`)
   * complete even if the initiating document is being torn down, which is
   * exactly `keepalive`'s documented purpose (same mechanism as
   * `navigator.sendBeacon`) — closing the gap without touching the guard,
   * without persisting the access token, and without changing rotation
   * semantics server-side. Only ever set on `authApi.refresh()` below — the
   * body is empty (well under the ~64KB keepalive request cap) and the
   * call has no side effect the rest of the app depends on completing
   * before the next paint. */
  keepalive?: boolean;
}

async function apiFetch<T>(
  path: string,
  // `ZodType<T, ZodTypeDef, any>` (not the `ZodType<T>` shorthand, which
  // fixes Input=Output=T): a schema with a `.default(...)` anywhere inside
  // it (e.g. `meResourceDetailSchema`'s NOTE payload `attachments`) has an
  // Input type narrower than its Output type `T`. The shorthand form
  // requires TS to unify both against the same `T` simultaneously, which
  // silently produces a widened/incorrect inferred `T` at call sites that
  // rely on inference, and an outright type error at call sites (like
  // `catalogueGuestApi.resourceDetail`) that pin `T` explicitly. Widening
  // the Input param to `any` here is the standard zod pattern for a
  // schema-typed generic helper and doesn't loosen anything callers see —
  // `T` (the Output/return type) is unaffected.
  schema: ZodType<T, z.ZodTypeDef, any> | null,
  opts: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  const token = getAccessToken();
  if (token && opts.auth !== false) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${apiBase}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      credentials: 'include',
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
      keepalive: opts.keepalive,
    });
  } catch (err) {
    throw new NetworkError('Network request failed', { cause: err });
  }

  if (!res.ok) {
    let problem: ProblemDetails | null = null;
    try {
      const data: unknown = await res.json();
      if (isProblemDetails(data)) problem = data;
    } catch {
      // Non-JSON / empty error body — leave `problem` null; the status-based
      // fallback message below still gives the user something actionable.
    }
    const retryAfterHeader = res.headers.get('Retry-After');
    const retryAfterSeconds =
      retryAfterHeader !== null && /^\d+$/.test(retryAfterHeader) ? Number(retryAfterHeader) : null;
    throw new ApiError(
      res.status,
      problem,
      problem?.detail ?? problem?.title ?? `Request failed (${res.status})`,
      retryAfterSeconds,
    );
  }

  if (res.status === 204) return undefined as T;
  const data: unknown = await res.json().catch(() => undefined);
  return schema ? schema.parse(data) : (data as T);
}

// ─── Auth surface (spec §6.1) ─────────────────────────────────────────────

export const authApi = {
  login(body: LoginRequest): Promise<LoginResponse> {
    return apiFetch('/auth/login', loginResponseSchema, { method: 'POST', body, auth: false });
  },
  register(body: RegisterRequest): Promise<RegisterResponse> {
    return apiFetch('/auth/register', registerResponseSchema, {
      method: 'POST',
      body,
      auth: false,
    });
  },
  verifyEmail(body: VerifyEmailRequest): Promise<LoginResponse> {
    return apiFetch('/auth/verify-email', loginResponseSchema, {
      method: 'POST',
      body,
      auth: false,
    });
  },
  resendVerification(body: ResendVerificationRequest): Promise<GenericAckResponse> {
    return apiFetch('/auth/resend-verification', genericAckResponseSchema, {
      method: 'POST',
      body,
      auth: false,
    });
  },
  acceptInvite(body: InviteAcceptRequest): Promise<LoginResponse> {
    return apiFetch('/auth/invite/accept', loginResponseSchema, {
      method: 'POST',
      body,
      auth: false,
    });
  },
  forgotPassword(body: PasswordForgotRequest): Promise<GenericAckResponse> {
    return apiFetch('/auth/password/forgot', genericAckResponseSchema, {
      method: 'POST',
      body,
      auth: false,
    });
  },
  resetPassword(body: PasswordResetRequest): Promise<GenericAckResponse> {
    return apiFetch('/auth/password/reset', genericAckResponseSchema, {
      method: 'POST',
      body,
      auth: false,
    });
  },
  me(): Promise<AuthMeResponse> {
    return apiFetch('/auth/me', authMeResponseSchema, { method: 'GET' });
  },
  refresh(): Promise<RefreshResponse> {
    // keepalive: true — BOOT-RACE-1, see `RequestOptions.keepalive` above.
    return apiFetch('/auth/refresh', refreshResponseSchema, {
      method: 'POST',
      auth: false,
      keepalive: true,
    });
  },
  logout(): Promise<GenericAckResponse> {
    return apiFetch('/auth/logout', genericAckResponseSchema, { method: 'POST', auth: false });
  },
};

export type AuthApi = typeof authApi;

// ─── Public curriculum-picker surface (AUTH-01) ────────────────────────────
// Anonymous, unauthenticated lookups feeding the registration form's optional
// exam-board/level selects (spec `docs/design/
// AUTH-01-registration-board-level-decision.md` §2) — a deliberately narrow
// projection, not the full authoring `@eskayscience/api-client` surface.

export const curriculumPublicApi = {
  examBoards(): Promise<ExamBoardsPublicResponse> {
    return apiFetch('/exam-boards/public', examBoardsPublicResponseSchema, { auth: false });
  },
  /** Omit `examBoardId` for all published levels. */
  levels(examBoardId?: string): Promise<LevelsPublicResponse> {
    const qs = examBoardId ? `?examBoardId=${encodeURIComponent(examBoardId)}` : '';
    return apiFetch(`/levels/public${qs}`, levelsPublicResponseSchema, { auth: false });
  },
};

export type CurriculumPublicApi = typeof curriculumPublicApi;

// ─── Public catalogue surface — guest resource detail (guest-funnel-bugs) ──
// `GET /v1/catalogue/public/resources/:id` — anonymous, no-auth sibling of
// the authenticated `GET /v1/me/resources/:id` (`@eskayscience/api-client`'s
// `meResourcesApi.getV1MeResourcesId`). Not yet codegen'd into
// `@eskayscience/api-client` (added on this same branch's backend half), so
// this is a hand-rolled client in the same posture as `metaApi`/the Slice-C
// `packages*Api` below — swap to the generated client once `pnpm codegen`
// picks up the new path. Response body is the SAME `MeResourceDetail`
// discriminated union `GET /v1/me/resources/:id` returns (see
// `apps/api/src/routes/catalogue-public.ts`), so it's validated here against
// the identical shared zod schema and typed identically — `data/player.ts`'s
// `toPlayerView` projector accepts either.
export const catalogueGuestApi = {
  /** Fail-closed `404 RESOURCE_NOT_VISIBLE` (never `403`) for any id that
   * doesn't resolve to a published, free-preview SIMULATION — same
   * `ApiError` shape every other `apiFetch` caller already handles. */
  resourceDetail(resourceId: string, signal?: AbortSignal): Promise<MeResourceDetail> {
    // Explicit type argument (not left to inference): `meResourceDetailSchema`
    // has a `.default([])` deep inside its NOTE payload variant, so its zod
    // Input/Output types diverge — left to infer, TS unifies `apiFetch`'s
    // `schema: ZodType<T> | null` param against BOTH Input and Output
    // simultaneously and derives a widened `T` that doesn't structurally
    // match the `MeResourceDetail` alias declared as this function's return
    // type. Pinning `T` explicitly sidesteps that inference ambiguity.
    return apiFetch<MeResourceDetail>(
      `/catalogue/public/resources/${encodeURIComponent(resourceId)}`,
      meResourceDetailSchema,
      { method: 'GET', auth: false, signal },
    );
  },
};

export type CatalogueGuestApi = typeof catalogueGuestApi;

// ─── Command channel surface (P2-25, design §5) ────────────────────────────
// Transport only — the poll cadence/backoff/dispatch logic lives in
// `./command-poll.ts`, and the store in `../stores/commands`. Both are
// `/v1/me/*` routes, guarded the same way as every other `/v1/me/*` call
// (Bearer token attached automatically by `apiFetch` above).

// Deliberately lenient top-level envelope schema — NOT `commandPollResponseSchema` from
// `@eskayscience/shared/dto`. That schema's `commands` field is `z.array(userCommandSchema)`,
// so a single whole-array `.parse()` against it throws on the FIRST element that fails the
// discriminated union (e.g. a future 5th command type). Design §4/§5.4 requires per-element
// forward-compat parsing instead ("never a single whole-array .parse()"), so the transport
// layer here only guarantees the envelope shape (with `.catch()` fallbacks so even a
// malformed envelope never throws — §5.2 "malformed body" is a backoff-success case, not an
// error) and leaves each element as `unknown`; `./command-poll.ts` runs `userCommandSchema
// .safeParse()` per element and drops/acks/logs anything that doesn't fit.
const commandPollEnvelopeSchema = z.object({
  commands: z.array(z.unknown()).catch([]),
  hasMore: z.boolean().catch(false),
  nextPollMs: z.number().catch(25000),
});
export type RawCommandPollResponse = z.infer<typeof commandPollEnvelopeSchema>;

export const commandsApi = {
  /** `GET /v1/me/commands` — pending commands for the caller only. Returns the envelope with
   * `commands` still `unknown[]` — see the schema comment above for why. */
  poll(signal?: AbortSignal): Promise<RawCommandPollResponse> {
    // Let T infer from the schema itself (its zod *input* type has optional fields because
    // of `.catch()`, which doesn't structurally satisfy `ZodType<RawCommandPollResponse>`)
    // then assert to the public alias — the `.catch()` defaults guarantee the runtime output
    // always matches `RawCommandPollResponse` regardless of what was actually on the wire.
    return apiFetch('/me/commands', commandPollEnvelopeSchema, {
      method: 'GET',
      signal,
    }) as Promise<RawCommandPollResponse>;
  },
  /** `POST /v1/me/commands/ack` — batch ack, always `204`/`undefined` for any
   * well-formed, authenticated request (design §3.2/§3.3 — idempotent). */
  ack(ids: CommandAckRequest['ids']): Promise<undefined> {
    return apiFetch('/me/commands/ack', null, {
      method: 'POST',
      body: { ids } satisfies CommandAckRequest,
    });
  },
};

export type CommandsApi = typeof commandsApi;

// ─── Meta surface (running-version read-only display) ─────────────────────
// `GET /v1/meta/version` (backend branch `feat/version-meta-endpoint`, public/
// no-auth) — used only by the admin Settings screen's read-only version
// display. Deliberately a local zod schema (not from `@eskayscience/shared/
// dto` or the generated `@eskayscience/api-client`) since the endpoint isn't
// merged/codegen'd yet; the contract `{ version, sha }` is fixed by the
// backend task, so swap to the generated client once codegen picks it up.
const versionResponseSchema = z.object({
  version: z.string(),
  sha: z.string(),
});
export type VersionResponse = z.infer<typeof versionResponseSchema>;

export const metaApi = {
  /** `GET /v1/meta/version` — public, no auth required. */
  version(signal?: AbortSignal): Promise<VersionResponse> {
    return apiFetch('/meta/version', versionResponseSchema, { method: 'GET', auth: false, signal });
  },
};

export type MetaApi = typeof metaApi;

// ─── Packages surface (ADR-packages-entitlements.md §6, Slice C) ──────────
// `/v1/packages` + `/v1/students/:id/packages` + `/v1/student-packages/:id`
// — TEACHER_OR_ADMIN, backend branch `feat/pkg-slice-c-backend`, NOT YET
// merged/codegen'd into `@eskayscience/shared/dto` or
// `@eskayscience/api-client` at the time this was written (Slice A's schema
// merged to `develop`; the DTOs/routes have not). Deliberately a hand-rolled
// local schema/client (same posture as `metaApi` above) so this screen does
// not depend on an unmerged sibling branch — swap to the generated client
// once Slice C backend merges and `pnpm codegen` picks up the new tag.

export const PackageGrantType = { BOARD: 'BOARD', LEVEL: 'LEVEL', SUBJECT: 'SUBJECT' } as const;
export type PackageGrantType = (typeof PackageGrantType)[keyof typeof PackageGrantType];

export const PackageStatus = { DRAFT: 'DRAFT', ACTIVE: 'ACTIVE', INACTIVE: 'INACTIVE' } as const;
export type PackageStatus = (typeof PackageStatus)[keyof typeof PackageStatus];

export const StudentPackageStatus = {
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  REVOKED: 'REVOKED',
  PENDING_PAYMENT: 'PENDING_PAYMENT',
} as const;
export type StudentPackageStatus = (typeof StudentPackageStatus)[keyof typeof StudentPackageStatus];

const packagePriceSchema = z.object({
  currency: z.string(),
  amountMinor: z.number().int(),
});
export type PackagePrice = z.infer<typeof packagePriceSchema>;

// One `PackageGrant` row on the wire (docs/ADR-multi-grant-packages.md §5.3).
const packageGrantDtoSchema = z.object({
  id: z.string(),
  position: z.number().int(),
  grantType: z.enum(['BOARD', 'LEVEL', 'SUBJECT']),
  examBoardId: z.string().nullable(),
  levelId: z.string().nullable(),
  subjectId: z.string().nullable(),
});
export type PackageGrantDto = z.infer<typeof packageGrantDtoSchema>;

const packageDtoSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** @deprecated the grant at `position 0` — read `grants` instead
   * (docs/ADR-multi-grant-packages.md §1.3/§5.3). */
  grantType: z.enum(['BOARD', 'LEVEL', 'SUBJECT']),
  /** @deprecated see `grantType`. */
  examBoardId: z.string().nullable(),
  /** @deprecated see `grantType`. */
  levelId: z.string().nullable(),
  /** @deprecated see `grantType`. */
  subjectId: z.string().nullable(),
  /** All grant targets this package confers, ordered by `position`
   * (docs/ADR-multi-grant-packages.md §5.3). `grants[0]` is exactly the
   * deprecated legacy quad above. */
  grants: z.array(packageGrantDtoSchema).default([]),
  grantCount: z.number().int().default(0),
  durationDays: z.number().int(),
  status: z.enum(['DRAFT', 'ACTIVE', 'INACTIVE']),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdById: z.string(),
  prices: z.array(packagePriceSchema),
  /** Count of currently-LIVE `StudentPackage` rows — populated on
   * `GET /v1/packages/:id` and create/update responses only (omitted on
   * `list`, docs/ADR-multi-grant-packages.md §5.3). Drives the S4
   * live-subscriber warning dialog. */
  activeSubscriberCount: z.number().int().optional(),
});
export type PackageDto = z.infer<typeof packageDtoSchema>;

const packageListResponseSchema = z.object({ items: z.array(packageDtoSchema) });
export type PackageListResponse = z.infer<typeof packageListResponseSchema>;

const studentPackageDtoSchema = z.object({
  id: z.string(),
  studentId: z.string(),
  packageId: z.string(),
  status: z.string(),
  startsAt: z.string(),
  expiresAt: z.string(),
  source: z.string(),
  pricePaidMinor: z.number().int().nullable(),
  currencyPaid: z.string().nullable(),
  subscriptionId: z.string().nullable(),
  grantedById: z.string().nullable(),
  createdAt: z.string(),
  package: packageDtoSchema.optional(),
});
export type StudentPackageDto = z.infer<typeof studentPackageDtoSchema>;

const studentPackageListResponseSchema = z.object({ items: z.array(studentPackageDtoSchema) });
export type StudentPackageListResponse = z.infer<typeof studentPackageListResponseSchema>;

/** `POST /v1/students/packages/batch` response — one entry per requested
 * `studentId`, in request order (`apps/api/src/routes/students.ts`'s
 * "N+1-safe Students table" route). A student with 0 packages comes back
 * with `packages: []`, never omitted. */
const studentPackagesBatchResponseSchema = z.object({
  items: z.array(
    z.object({
      studentId: z.string(),
      packages: z.array(studentPackageDtoSchema),
    }),
  ),
});
export type StudentPackagesBatchResponse = z.infer<typeof studentPackagesBatchResponseSchema>;

/** The three-way discriminated grant target — mirrors the backend's
 * `grantTargetShape` (`packages/shared/src/dto/packages.ts` on the backend
 * branch): exactly one of `examBoardId`/`levelId`/`subjectId` set, matching
 * `grantType`. */
export interface PackageGrantTarget {
  grantType: PackageGrantType;
  examBoardId?: string;
  levelId?: string;
  subjectId?: string;
}

/** `grants[]` (docs/ADR-multi-grant-packages.md §5.1) — a package now
 * grants the UNION of N of these instead of exactly one. `PackagesView.vue`
 * sends ONLY `grants[]` (never the legacy flat `PackageGrantTarget` fields
 * at the same time — the two shapes are mutually exclusive on the wire,
 * `400 GRANT_SHAPE_CONFLICT` if both are present). The legacy fields stay
 * on these interfaces (via `PackageGrantTarget`) purely so older callers of
 * this client keep compiling; this view no longer sets them. */
export interface PackageCreateRequest extends Partial<PackageGrantTarget> {
  code: string;
  name: string;
  description?: string | null;
  durationDays: number;
  status?: PackageStatus;
  prices: PackagePrice[];
  grants?: PackageGrantTarget[];
}

export interface PackageUpdateRequest extends Partial<PackageGrantTarget> {
  code?: string;
  name?: string;
  description?: string | null;
  durationDays?: number;
  status?: PackageStatus;
  prices?: PackagePrice[];
  /** Full-set replace, never a partial merge (ADR §5.2). */
  grants?: PackageGrantTarget[];
}

export const packagesApi = {
  /** `GET /v1/packages` — TEACHER_OR_ADMIN. */
  list(includeArchived: boolean, signal?: AbortSignal): Promise<PackageListResponse> {
    const qs = includeArchived ? '?includeArchived=true' : '';
    return apiFetch(`/packages${qs}`, packageListResponseSchema, { method: 'GET', signal });
  },
  /** `GET /v1/packages/:id` — TEACHER_OR_ADMIN. Includes
   * `activeSubscriberCount` (list does not) — fetched on edit-open so the
   * S4 live-subscriber warning dialog has a real number before the admin
   * even starts editing grants. */
  get(id: string, signal?: AbortSignal): Promise<PackageDto> {
    return apiFetch(`/packages/${encodeURIComponent(id)}`, packageDtoSchema, {
      method: 'GET',
      signal,
    });
  },
  /** `POST /v1/packages` — TEACHER_OR_ADMIN. */
  create(body: PackageCreateRequest, idempotencyKey: string): Promise<PackageDto> {
    return apiFetch('/packages', packageDtoSchema, { method: 'POST', body, idempotencyKey });
  },
  /** `PATCH /v1/packages/:id` — TEACHER_OR_ADMIN. May reject a grant-target
   * edit with `409 PACKAGE_HAS_SUBSCRIBERS` (ADR §6.3) if the package has
   * live subscribers — callers should catch {@link ApiError} and branch on
   * `err.code`. */
  update(id: string, body: PackageUpdateRequest, idempotencyKey: string): Promise<PackageDto> {
    return apiFetch(`/packages/${encodeURIComponent(id)}`, packageDtoSchema, {
      method: 'PATCH',
      body,
      idempotencyKey,
    });
  },
  /** `DELETE /v1/packages/:id` — archive (soft-delete), TEACHER_OR_ADMIN. */
  archive(id: string, idempotencyKey: string): Promise<PackageDto> {
    return apiFetch(`/packages/${encodeURIComponent(id)}`, packageDtoSchema, {
      method: 'DELETE',
      idempotencyKey,
    });
  },
};
export type PackagesApi = typeof packagesApi;

export const studentPackagesApi = {
  /** `GET /v1/students/:id/packages` — this student's assignment history,
   * TEACHER_OR_ADMIN. */
  listForStudent(studentId: string, signal?: AbortSignal): Promise<StudentPackageListResponse> {
    return apiFetch(
      `/students/${encodeURIComponent(studentId)}/packages`,
      studentPackageListResponseSchema,
      { method: 'GET', signal },
    );
  },
  /** `POST /v1/students/:id/packages` — manual assignment (ADR §6.1/§6.4),
   * TEACHER_OR_ADMIN. `startsAt` is always server-derived; never send one. */
  assign(
    studentId: string,
    packageId: string,
    idempotencyKey: string,
  ): Promise<StudentPackageDto> {
    return apiFetch(
      `/students/${encodeURIComponent(studentId)}/packages`,
      studentPackageDtoSchema,
      { method: 'POST', body: { packageId }, idempotencyKey },
    );
  },
  /** `PATCH /v1/student-packages/:id` — revoke (status change, never a
   * delete — ADR §6.2), TEACHER_OR_ADMIN. The route takes no request body
   * (`StudentPackageService.revoke` is unconditional); a PATCH with an empty
   * body is the correct call. */
  revoke(id: string, idempotencyKey: string): Promise<StudentPackageDto> {
    return apiFetch(`/student-packages/${encodeURIComponent(id)}`, studentPackageDtoSchema, {
      method: 'PATCH',
      body: {},
      idempotencyKey,
    });
  },
  /** `POST /v1/students/packages/batch` — N+1-safe multi-student package
   * lookup (1-200 ids), TEACHER_OR_ADMIN, same privilege tier as
   * `listForStudent` above (no new gap). Used by the admin Students table to
   * populate a "current package" column with ONE call per rendered page,
   * never one call per row. */
  listForStudents(
    studentIds: string[],
    signal?: AbortSignal,
  ): Promise<StudentPackagesBatchResponse> {
    return apiFetch('/students/packages/batch', studentPackagesBatchResponseSchema, {
      method: 'POST',
      body: { studentIds },
      signal,
    });
  },
};
export type StudentPackagesApi = typeof studentPackagesApi;

// ─── Assets surface (P5-17, mp4-direct-upload design doc §7 Wave 2 task 8) ──
// `GET /v1/assets/:id` — TEACHER_OR_ADMIN. The route handler landed on
// `apps/api/src/routes/assets.ts` but was never registered in
// `apps/api/src/routes/openapi.ts`, so `pnpm codegen` does not emit it into
// `@eskayscience/api-client` yet (verified on this branch — regenerating
// picks up no new method). Same posture as `metaApi`/`packagesApi` above:
// a hand-rolled client against the shared `assetResponseSchema` so
// `VideoEditor.vue` can display the attached filename when re-opening an
// existing saved mp4 lecture without blocking on that backend gap — swap to
// the generated `assetsApi.useGetV1AssetsId` once openapi.ts registers the
// route and codegen picks it up.
export const assetsWebApi = {
  /** `GET /v1/assets/:id` — TEACHER_OR_ADMIN, 404 RESOURCE_NOT_VISIBLE if
   * missing (see `assets.ts`'s `ASSET_NOT_FOUND_DETAIL`). */
  get(id: string, signal?: AbortSignal): Promise<AssetResponse> {
    return apiFetch(`/assets/${encodeURIComponent(id)}`, assetResponseSchema, {
      method: 'GET',
      signal,
    });
  },
};
export type AssetsWebApi = typeof assetsWebApi;
